/**
 * «Planilla» on the screen (the owner, 2026-10-12: «some people want to be paid weekly, others are
 * going to be paid BY the amount of days they work so not like a regular employee»).
 *
 * The books decide how a person is paid and what a run is (`projections/planilla.ts` in core, the
 * stretches of days in `planilla.ts` in shared). This says it in words and offers only what they
 * accept: what was agreed and for what, what somebody costs the shop, the days a run pays and the
 * one that comes next, and the line a run sends for each person, worked out with the books' own
 * functions so the sheet cannot promise a different number.
 */

import {
  PAGOS_POR_ANIO,
  equivalenteMensual,
  formatBp,
  formatMoney,
  formatQuantity,
  mesSiguiente,
  mulDiv,
  parseMoney,
  parseQuantity,
  rangoDeQuincena,
  rangoDeSemana,
  rangoDelMes,
  subMoney,
  sumarDias,
  type Centavos,
  type Envelope,
  type FrecuenciaPago,
  type Milli,
  type Rango,
} from '@abastece/shared';
import {
  acumuladoDelAnio,
  calcularPlanilla,
  costoDeLinea,
  diasDeLaCorrida,
  diasDesdeIngreso,
  diasPagados,
  lineaDeCorridaEnLosLibros,
  lugarEnElLog,
  pagoDe,
  partesDeCorrida,
  rangoDeCorrida,
  revertirLinea,
  sueldoDeCorrida,
  sueldoDelPeriodo,
  type Empleado,
  type Gasto,
  type PagoAguinaldo,
  type PagoCotizaciones,
  type PayrollRates,
  type PayrollResult,
  type Planilla,
  type PlanillaLinea,
} from '@abastece/core';
import { localeActual, traducir as t, type Clave } from '../i18n/index.js';

/** What an agreed amount is for: a day worked, or one week, fifteen days or month of pay. */
export type Por = 'dia' | FrecuenciaPago;

export const porDe = (e: Empleado): Por => {
  const pago = pagoDe(e);
  return pago.porDia ? 'dia' : pago.frecuencia;
};

/** What was agreed: so much a day, a week or fifteen days; or the monthly salary. */
export function montoAcordado(e: Empleado): Centavos {
  const pago = pagoDe(e);
  if (pago.porDia || pago.frecuencia !== 'mensual') return pago.tarifa ?? sueldoDelPeriodo(e, pago.frecuencia);
  return e.salarioMensual;
}

/** How many of each make a year: a day's pay counts thirty to the month, as the books count it (`equivalenteMensual`). */
const VECES_AL_ANIO: Record<Por, number> = { dia: 360, ...PAGOS_POR_ANIO };

/**
 * An amount for one thing said for another: C$ 9,000 a month is C$ 4,500 every fifteen days, C$
 * 2,076.92 a week, C$ 300 a day. What the hire sheet fills in beside what is typed, and how it keeps
 * the pay when the schedule changes.
 */
export const enOtraUnidad = (monto: Centavos, de: Por, a: Por): Centavos =>
  de === a ? monto : mulDiv(monto, VECES_AL_ANIO[de], VECES_AL_ANIO[a]);

/**
 * What a hire or a change of pay records for an amount typed for `por`: what is agreed for each day,
 * week or fifteen days (`tarifa`, none for a monthly salary), and what that comes to a month
 * (`salarioMensual`). A month's salary typed is kept as typed; anything else comes to a month as the
 * books count it.
 *
 * The amount used to be whatever the one box held, for whatever the schedule picked said: C$ 9,000
 * typed as the salary, with «Quincenal» picked before or after, was C$ 9,000 every fifteen days, C$
 * 18,000 a month, and the live shop's two people came out at twice their pay (2026-10-31).
 */
export function pagoAcordado(
  monto: Centavos,
  por: Por,
  como: { porDia: boolean; frecuencia: FrecuenciaPago },
): { tarifa: Centavos | null; salarioMensual: Centavos } {
  const unidad: Por = como.porDia ? 'dia' : como.frecuencia;
  if (unidad === 'mensual') return { tarifa: null, salarioMensual: enOtraUnidad(monto, por, 'mensual') };
  const tarifa = enOtraUnidad(monto, por, unidad);
  return { tarifa, salarioMensual: por === 'mensual' && !como.porDia ? monto : (equivalenteMensual(como.frecuencia, como.porDia, tarifa) as Centavos) };
}

/**
 * What «Editar» opens with, so saving it unchanged changes nothing: a day's pay for somebody paid by
 * the day; for a fixed pay, the month's salary, what each payday pays worked out beside it; and when
 * the two the books keep do not say the same (a phone that has not updated wrote one), what each
 * payday pays, as agreed.
 */
export function montoParaEditar(e: Empleado): { monto: Centavos; por: Por } {
  const pago = pagoDe(e);
  if (pago.porDia) return { monto: montoAcordado(e), por: 'dia' };
  if (pago.tarifa === null || pago.frecuencia === 'mensual' || enOtraUnidad(e.salarioMensual, 'mensual', pago.frecuencia) === pago.tarifa) {
    return { monto: e.salarioMensual, por: 'mensual' };
  }
  return { monto: pago.tarifa, por: pago.frecuencia };
}

/**
 * What «Gastos» holds as salaries that may be a run's pay already: an expense of «Sueldos» not
 * voided, from the run's first day on, when pay for those days is handed over. Booked as salaries
 * too, the run would count each of them again: on the live shop the owner, with no way to say a
 * month was paid, wrote it down there (2026-10-31: «Pago del mes a Simon», C$ 9,000).
 */
export function sueldosEnGastos(gastos: readonly Gasto[], rango: Rango): Gasto[] {
  return gastos
    .filter((g) => !g.anulado && g.categoria === 'sueldos' && g.fecha >= rango.desde)
    .sort((a, b) => a.fecha.localeCompare(b.fecha));
}

/**
 * What is owed to the INSS (the share withheld from each person and the shop's own), to the INATEC,
 * and to the DGI (the income tax withheld): what the runs and settlements of months up to `hasta`
 * booked, less everything paid to them (2026-10-31). The books' «INSS por pagar», «INATEC por
 * pagar» and «IR retenido por pagar» to the centavo, without `hasta` (`cuadres`). Nothing could pay
 * them: they were owed for ever, and what the shop paid the INSS went in «Gastos», where the shop's
 * share of it was spent twice.
 */
export function cotizacionesPorPagar(
  corridas: readonly Pick<Planilla, 'periodo' | 'totalInssLaboral' | 'totalInssPatronal' | 'totalInatec' | 'totalIr'>[],
  pagos: readonly Pick<PagoCotizaciones, 'inss' | 'inatec' | 'ir'>[],
  hasta?: string,
): { inss: Centavos; inatec: Centavos; ir: Centavos } {
  const debe = { inss: 0, inatec: 0, ir: 0 };
  for (const p of corridas) {
    if (hasta !== undefined && p.periodo > hasta) continue;
    debe.inss += p.totalInssLaboral + p.totalInssPatronal;
    debe.inatec += p.totalInatec;
    debe.ir += p.totalIr;
  }
  for (const x of pagos) {
    debe.inss -= x.inss;
    debe.inatec -= x.inatec;
    debe.ir -= x.ir;
  }
  return debe as { inss: Centavos; inatec: Centavos; ir: Centavos };
}

/** Vacation days rested as the books took them (`empleado.vacaciones`): whose, from when to when, how many in thousandths. */
export interface VacacionesApuntadas {
  empleadoId: string;
  desde: string;
  hasta: string;
  dias: number;
}

/**
 * The log's vacation days written down outside a run that `tuvo` says the books took: a run of those
 * days says so on the person's line, so they are not typed on it again (2026-10-31).
 */
export function vacacionesApuntadas<E extends Envelope & { estado: string }>(eventos: readonly E[], tuvo: (e: E) => boolean): VacacionesApuntadas[] {
  return eventos
    .filter((e) => e.type === 'empleado.vacaciones' && tuvo(e))
    .map((e) => {
      const p = e.payload as VacacionesApuntadas;
      return { empleadoId: p.empleadoId, desde: p.desde, hasta: p.hasta, dias: p.dias };
    });
}

/** An advance as the books took it (`empleado.adelanto`): whose, the day it was given, how much, and its place in the log. */
export interface AdelantoDado {
  empleadoId: string;
  fecha: string;
  monto: number;
  lugar: number;
}

/** The log's advances that `tuvo` says the books took (`tuvoEfecto`). */
export function adelantosDados<E extends Envelope & { estado: string }>(eventos: readonly E[], tuvo: (e: E) => boolean): AdelantoDado[] {
  return eventos
    .filter((e) => e.type === 'empleado.adelanto' && tuvo(e))
    .map((e) => {
      const p = e.payload as { empleadoId: string; fecha: string; monto: number };
      return { empleadoId: p.empleadoId, fecha: p.fecha, monto: p.monto, lugar: lugarEnElLog(e) };
    });
}

/**
 * The most a run written down once it was paid takes off what somebody owes in advances
 * (`adelantoMaximo`, 2026-10-31): what they owe, less what they were given after the day it was
 * paid. What is owed is the newest of what was given, the oldest taken off first; given after that
 * day, it was not taken off the money handed over then, and the next run takes it. It took it off
 * January's run, and the payment booked that much less than had been handed over.
 */
export function adelantoAlDia(e: Pick<Empleado, 'id' | 'adelantosPendientes'>, dados: readonly AdelantoDado[], fecha: string): Centavos {
  const despues = dados.reduce((t, a) => (a.empleadoId === e.id && a.fecha > fecha ? t + a.monto : t), 0);
  return Math.max(0, (e.adelantosPendientes ?? 0) - despues) as Centavos;
}

/**
 * The first day a run or a settlement can say it was paid: its own day, or that of the newest advance
 * given, before it was written down (`lugar`), to somebody it took advances off. Paid before, the
 * money handed over would have had an advance taken off it that nobody had been given yet
 * (2026-10-31). That advance may have waited for a later run, when the net could not take them all:
 * then a day too late, never one too early.
 */
export function primerDiaDePago(
  p: Pick<Planilla, 'fecha' | 'lineas'>,
  dados: readonly AdelantoDado[],
  lugar: number,
): { fecha: string; nombre: string | null } {
  let fecha = p.fecha;
  let nombre: string | null = null;
  for (const l of p.lineas) {
    if ((l.adelantoDescontado ?? 0) <= 0) continue;
    for (const a of dados) {
      if (a.empleadoId === l.empleadoId && a.lugar < lugar && a.fecha > fecha) {
        fecha = a.fecha;
        nombre = l.nombre;
      }
    }
  }
  return { fecha, nombre };
}

/** «por día», «por semana», «por quincena», «al mes». */
export const cada = (por: Por): string => t(`planilla.cada.${por}` as Clave);

/** «C$ 350.00 por día». */
export const dichoAcordado = (monto: Centavos, por: Por): string => `${formatMoney(monto)} ${cada(por)}`;

/**
 * What a pay costs the shop on top of itself: INSS and INATEC when they apply, and the aguinaldo
 * and vacations it accrues when those do. The income tax is theirs, so how often they are paid
 * does not change it.
 */
export function costoDePago(
  monto: Centavos,
  pago: { conInss: boolean; conPrestaciones: boolean },
  rates: PayrollRates,
): Centavos {
  const r = calcularPlanilla({ bruto: monto }, rates, pago);
  return (r.costoTotal + r.provisionAguinaldo + r.provisionVacaciones) as Centavos;
}

/**
 * What a run cost the business, as its entry books it and «Correr planilla» said before it was run:
 * the pay, the employer's INSS and INATEC, and the aguinaldo and vacations it set aside, less what
 * of its payout earlier runs had already set aside (`costoDeLinea`). The run's own `costoTotal`
 * leaves the provisions out, and the same run read C$ 16,820 there and C$ 14,820 on its card
 * (2026-10-13); and the aguinaldo December paid out of what was set aside was counted again, C$
 * 28,820 for C$ 16,820 booked (2026-10-20).
 */
export function costoDeCorrida(p: Pick<Planilla, 'lineas'>): Centavos {
  return p.lineas.reduce((a, l) => a + costoDeLinea(l), 0) as Centavos;
}

/** What «Costo mensual» is made of (2026-10-31, its bubble): the wages, the shop's INSS and INATEC, what is set aside. */
export interface PartesDelCosto {
  salarios: Centavos;
  inssPatronal: Centavos;
  inatec: Centavos;
  aguinaldo: Centavos;
  vacaciones: Centavos;
}

/**
 * What the people on a fixed pay cost a month, by their monthly salary — what a week or fifteen
 * days comes to in a month — and what that is made of, which adds up to it. Somebody paid by the
 * day costs what they work, which nobody knows before: they are counted apart.
 */
export function costoMensual(
  activos: readonly Empleado[],
  rates: PayrollRates,
): { costo: Centavos; porDia: number; partes: PartesDelCosto } {
  let costo = 0;
  let porDia = 0;
  const partes = { salarios: 0, inssPatronal: 0, inatec: 0, aguinaldo: 0, vacaciones: 0 };
  for (const e of activos) {
    const pago = pagoDe(e);
    if (pago.porDia) {
      porDia += 1;
      continue;
    }
    costo += costoDePago(e.salarioMensual, pago, rates);
    const r = calcularPlanilla({ bruto: e.salarioMensual }, rates, pago);
    partes.salarios += r.gravable + r.exento;
    partes.inssPatronal += r.inssPatronal;
    partes.inatec += r.inatec;
    partes.aguinaldo += r.provisionAguinaldo;
    partes.vacaciones += r.provisionVacaciones;
  }
  return { costo: costo as Centavos, porDia, partes: partes as PartesDelCosto };
}

// ---------------------------------------------------------------------------------------------
// The days of a run
// ---------------------------------------------------------------------------------------------

/** The run after one that paid `r`: the next week, the other half of the month, the next month. */
export function rangoSiguiente(frecuencia: FrecuenciaPago, r: Rango): Rango {
  const periodo = r.hasta.slice(0, 7);
  switch (frecuencia) {
    case 'semanal':
      return rangoDeSemana(sumarDias(r.hasta, 7));
    case 'quincenal':
      return r.desde.endsWith('-01') ? rangoDeQuincena(periodo, true) : rangoDeQuincena(mesSiguiente(periodo), false);
    case 'mensual':
      return rangoDelMes(mesSiguiente(periodo));
  }
}

/** The days a run of today pays: the week that ends today, the half of the month, the month. */
export function rangoDeHoy(frecuencia: FrecuenciaPago, hoy: string): Rango {
  const periodo = hoy.slice(0, 7);
  switch (frecuencia) {
    case 'semanal':
      return rangoDeSemana(hoy);
    case 'quincenal':
      return rangoDeQuincena(periodo, Number(hoy.slice(8, 10)) > 15);
    case 'mensual':
      return rangoDelMes(periodo);
  }
}

/**
 * The days a new run of this schedule offers: where the last one left off, so a week keeps its
 * payday and none is skipped or paid twice; with none before, today's.
 */
export function rangoSugerido(frecuencia: FrecuenciaPago, corridas: readonly Planilla[], hoy: string): Rango {
  const ultima = corridas
    // A run voided by a correction paid nobody: its days are offered again.
    .filter((p) => !p.esLiquidacion && !anulada(p) && (p.frecuencia ?? 'mensual') === frecuencia)
    .map(rangoDeCorrida)
    .reduce<Rango | null>((a, r) => (a === null || r.hasta > a.hasta ? r : a), null);
  return ultima ? rangoSiguiente(frecuencia, ultima) : rangoDeHoy(frecuencia, hoy);
}

const comoFecha = (f: string) => new Date(`${f}T12:00:00Z`);

/** «6 – 12 de oct de 2026», «Sep 28 – Oct 4, 2026». */
export function rangoLegible(r: Rango): string {
  const formato = new Intl.DateTimeFormat(localeActual(), { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
  return formato.formatRange(comoFecha(r.desde), comoFecha(r.hasta));
}

/** «octubre de 2026». */
export const mesLegible = (periodo: string): string =>
  new Date(`${periodo}-15T12:00:00Z`).toLocaleDateString(localeActual(), { month: 'long', year: 'numeric', timeZone: 'UTC' });

/** A run's name: «Planilla de la semana 6 – 12 de oct de 2026», «Planilla de octubre de 2026». */
export function tituloDeCorrida(frecuencia: FrecuenciaPago, r: Rango): string {
  if (frecuencia === 'semanal') return t('planilla.deLaSemana', { rango: rangoLegible(r) });
  if (frecuencia === 'quincenal') return t('planilla.deLaQuincena', { rango: rangoLegible(r) });
  return t('planilla.delPeriodo', { periodo: mesLegible(r.hasta.slice(0, 7)) });
}

export const tituloDe = (p: Planilla): string => tituloDeCorrida(p.frecuencia ?? 'mensual', rangoDeCorrida(p));

// ---------------------------------------------------------------------------------------------
// One person's line of a run
// ---------------------------------------------------------------------------------------------

/** What the run sheet holds for one person, as typed. */
export interface Borrador {
  empleadoId: string;
  incluir: boolean;
  /** Days worked, for somebody paid by the day. */
  dias: string;
  bruto: string;
  otrosGravables: string;
  vacacionesPagadas: string;
  diasVacaciones: string;
  aguinaldoPagado: string;
}

export const borradorVacio = (empleadoId: string): Borrador => ({
  empleadoId,
  incluir: true,
  dias: '',
  bruto: '',
  otrosGravables: '',
  vacacionesPagadas: '',
  diasVacaciones: '',
  aguinaldoPagado: '',
});

/** A line of `planilla.corrida`, as the registry takes it. */
export interface LineaEnviada {
  empleadoId: string;
  bruto?: number;
  dias?: number;
  otrosGravables: number;
  vacacionesPagadas: number;
  diasVacaciones: number;
  aguinaldoPagado: number;
  /** The most of what they owe in advances it takes off: those given by the day it was paid (`adelantoAlDia`). */
  adelantoMaximo?: number;
}

/** A run's fixed pay, in the words of its schedule. */
export const SALARIO_DE: Record<FrecuenciaPago, Clave> = {
  semanal: 'planilla.salarioDeLaSemana',
  quincenal: 'planilla.salarioDeLaQuincena',
  mensual: 'planilla.salarioDelMes',
};

/** `dias`: none typed for somebody paid by the day; `ilegible`: something typed that does not read as a number. */
export type LineaDeCorrida =
  | { falta: 'dias' | 'ilegible' }
  | {
      falta?: undefined;
      enviada: LineaEnviada;
      bruto: Centavos;
      calculo: PayrollResult;
      adelanto: Centavos;
      /** What the line costs the business, as its entry will book it (`costoDeLinea`). */
      costo: Centavos;
      /** The books' own line, figure by figure: what the run's breakdown says (`pasosDeLaLinea`). */
      libros: PlanillaLinea;
    };

/**
 * What one person's line sends and comes to. The books work out the same from what it sends: the
 * days times the day's pay, rounded as they round it, or the fixed pay of this run; and its income
 * tax goes on from the year's earlier runs in `corridas` (`irDeCorrida`).
 *
 * The pay is sent whole whenever the run is not a month (`bruto`), so a phone that has not updated,
 * which knows only months, books what was paid; a month's is sent only when typed, as before, or
 * when they started during it (`sueldoDeCorrida`), which such a phone would pay whole.
 *
 * `adelantoMaximo`: for a run written down once it was paid, what they owed of the advances given
 * by then (`adelantoAlDia`); sent when it is less than they owe.
 */
export function lineaDeCorrida(
  e: Empleado,
  b: Borrador,
  frecuencia: FrecuenciaPago,
  rango: Rango,
  rates: PayrollRates,
  corridas: readonly Planilla[] = [],
  adelantoMaximo?: number,
): LineaDeCorrida {
  const pago = pagoDe(e);
  // Blank is nothing; typed and unreadable is said, never paid as nothing (or as the whole salary,
  // or «1,5» days of vacation as NaN, which the books refused whole).
  const dinero = (texto: string) => (texto.trim() === '' ? 0 : parseMoney(texto));
  const extras = {
    otrosGravables: dinero(b.otrosGravables),
    vacacionesPagadas: dinero(b.vacacionesPagadas),
    diasVacaciones: b.diasVacaciones.trim() === '' ? 0 : parseQuantity(b.diasVacaciones),
    aguinaldoPagado: dinero(b.aguinaldoPagado),
  };
  if (Object.values(extras).some((x) => x === null || x < 0)) return { falta: 'ilegible' };
  const leidos = extras as { [K in keyof typeof extras]: number };

  let bruto: Centavos;
  let enviada: LineaEnviada;
  if (pago.porDia) {
    const dias = parseQuantity(b.dias);
    if (dias === null || dias <= 0 || pago.tarifa === null) return { falta: 'dias' };
    // Only a correction has their pay written: what their days were booked at, until the days change
    // (`borradorDeLinea`), so a day's pay changed since leaves a line nobody touched as it was.
    const escrito = b.bruto.trim() === '' ? undefined : parseMoney(b.bruto);
    if (escrito === null) return { falta: 'ilegible' };
    bruto = escrito ?? mulDiv(pago.tarifa, dias, 1000);
    enviada = { empleadoId: e.id, bruto, dias, ...leidos };
  } else {
    const escrito = b.bruto.trim() === '' ? undefined : parseMoney(b.bruto);
    if (escrito === null) return { falta: 'ilegible' };
    bruto = escrito ?? sueldoDeCorrida(e, frecuencia, rango);
    const enviarlo = escrito !== undefined || frecuencia !== 'mensual' || diasDesdeIngreso(e, rango) !== null;
    enviada = { empleadoId: e.id, ...(enviarlo ? { bruto } : {}), ...leidos };
  }
  const debe = e.adelantosPendientes ?? 0;
  if (adelantoMaximo !== undefined && adelantoMaximo < debe) enviada = { ...enviada, adelantoMaximo };

  const pagosPorAnio = PAGOS_POR_ANIO[frecuencia];
  const calculo = calcularPlanilla(
    {
      bruto,
      otrosGravables: leidos.otrosGravables as Centavos,
      vacacionesPagadas: leidos.vacacionesPagadas as Centavos,
      aguinaldoPagado: leidos.aguinaldoPagado as Centavos,
    },
    rates,
    {
      pagosPorAnio,
      conInss: pago.conInss,
      conPrestaciones: pago.conPrestaciones,
      delAnio: acumuladoDelAnio(corridas, e.id, rango.hasta.slice(0, 4)),
      partes: partesDeCorrida(pagosPorAnio),
    },
  );
  // The books' own line, for what it costs and what it takes off what is owed.
  const enLosLibros = lineaDeCorridaEnLosLibros(e, enviada, rates, frecuencia, rango, corridas);
  // The books take what they owe off the net, never past it, nor past what they owed of those given by then.
  const adelanto = Math.max(0, Math.min(debe, calculo.neto, enviada.adelantoMaximo ?? debe)) as Centavos;
  return { enviada, bruto, calculo, adelanto, costo: costoDeLinea(enLosLibros.linea), libros: enLosLibros.linea };
}

/** Whether the vacations and the aguinaldo can be paid on their line: they accrue, or did before. */
export const pagaPrestaciones = (e: Empleado): boolean =>
  pagoDe(e).conPrestaciones || e.aguinaldoAcumulado > 0 || e.vacacionesAcumuladas > 0;

/**
 * Whether their vacations show, on their card and their run's line: as the aguinaldo, and only while
 * vacations accrue in the shop (`vacacionesAcumulan`, off since the owner's 2026-10-31) or they still
 * have some set aside from before.
 */
export const muestraVacaciones = (e: Empleado, config: { vacacionesAcumulan?: boolean }): boolean =>
  pagaPrestaciones(e) && (config.vacacionesAcumulan !== false || e.vacacionesAcumuladas > 0 || e.diasVacacionesAcumulados > 0);

// ---------------------------------------------------------------------------------------------
// A run set right (the owner, 2026-10-31: «we need a way to be able to edit payroll runs from the
// runs tab for corrections»)
// ---------------------------------------------------------------------------------------------

/** A run a correction left with nobody on it (`planilla.corregida`): voided. */
export const anulada = (p: Pick<Planilla, 'esLiquidacion' | 'lineas'>): boolean => !p.esLiquidacion && p.lineas.length === 0;

// ---------------------------------------------------------------------------------------------
// The aguinaldo, paid on its own (the owner, 2026-10-31: «let's separate the pay aguinaldo function
// as a button on the payroll page and take that shit out of the normal payroll form»)
// ---------------------------------------------------------------------------------------------

/** Whose aguinaldo «Pagar aguinaldo» offers: whoever builds it up, or has some set aside from before, by name. */
export const quienesTienenAguinaldo = (activos: readonly Empleado[]): Empleado[] =>
  activos.filter(pagaPrestaciones).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));

/** An aguinaldo payment a correction left with nobody on it (`aguinaldo.corregido`): voided. */
export const aguinaldoAnulado = (p: Pick<PagoAguinaldo, 'lineas'>): boolean => p.lineas.length === 0;

const escritoONada = (monto: number): string => (monto === 0 ? '' : (monto / 100).toFixed(2));
const cantidadONada = (milesimas: number | undefined): string => (milesimas === undefined || milesimas === 0 ? '' : String(milesimas / 1000));

/**
 * What the sheet holds for somebody on a run being corrected: what their line booked, to be set
 * right. Their pay is written, so a salary or a day's pay changed since changes nothing nobody
 * touched; a day worker's is the pay their days were booked at, dropped once the days change.
 */
export const borradorDeLinea = (l: PlanillaLinea): Borrador => ({
  empleadoId: l.empleadoId,
  incluir: true,
  dias: cantidadONada(l.dias),
  bruto: (l.bruto / 100).toFixed(2),
  otrosGravables: escritoONada(l.otrosGravables),
  vacacionesPagadas: escritoONada(l.vacacionesPagadas),
  diasVacaciones: cantidadONada(l.diasVacaciones),
  aguinaldoPagado: escritoONada(l.aguinaldoPagado),
});

/**
 * Somebody as they were before the run being corrected, as the books take them back
 * (`revertirLinea`): what a later run paid out of what it set aside cannot leave them less than
 * nothing to pay from.
 */
export function antesDeLaCorrida(e: Empleado, l: PlanillaLinea, frecuencia: FrecuenciaPago, rango: Rango): Empleado {
  const estaba = revertirLinea(e, l, frecuencia, rango);
  return {
    ...estaba,
    aguinaldoAcumulado: Math.max(0, estaba.aguinaldoAcumulado) as Centavos,
    vacacionesAcumuladas: Math.max(0, estaba.vacacionesAcumuladas) as Centavos,
  };
}

/** The runs a corrected run's income tax goes on from: the year's before its days, as the books read them. */
export function corridasAntesDe(corridas: readonly Planilla[], p: Planilla): Planilla[] {
  const desde = rangoDeCorrida(p).desde;
  return corridas.filter((o) => o.id !== p.id && (o.esLiquidacion ? o.fecha : rangoDeCorrida(o).hasta) < desde);
}

/** The payment of a paid run, among the log's (`tuvo`: what the books took): what a correction changes with it. */
export function pagoDeLaCorrida<E extends Envelope & { estado: string }>(
  p: Pick<Planilla, 'id' | 'pagada' | 'pagadaEn'>,
  eventos: readonly E[],
  tuvo: (e: E) => boolean,
): E | null {
  if (!p.pagada) return null;
  return (
    eventos.find((e) => {
      if (e.type !== 'planilla.pagada' || !tuvo(e)) return false;
      const dice = e.payload as { planillaId?: string; fecha?: string };
      return dice.planillaId === p.id && dice.fecha === p.pagadaEn;
    }) ?? null
  );
}

/** One line of a run's breakdown, as on a receipt: which way it goes, what it is, how much. */
export interface PasoDeLaPlanilla {
  signo?: '+' | '−' | '=';
  clave: Clave;
  /** What the words say besides: a rate, the days a salary is for. */
  valores?: Record<string, string>;
  monto: Centavos;
}

/** A line that adds or takes away, or none when there is nothing on it. */
const pasoSi = (clave: Clave, monto: number, signo: '+' | '−', valores?: Record<string, string>): PasoDeLaPlanilla[] =>
  monto > 0 ? [{ signo, clave, ...(valores ? { valores } : {}), monto: monto as Centavos }] : [];

/**
 * What one person's line of a run comes to, a line each (the owner, 2026-10-31: «when we hit run
 * payroll can we see a calculation summary/breakdown of the payroll somewhere in that dialog?»): their
 * pay for these days, and for how many of them when they started during them; what else it pays;
 * what comes off it, their INSS, the income tax and the advances; and what is handed over. Read off
 * the books' own line (`libros`), so it says what the run will book.
 *
 * `escrito`: the pay was typed on the line, so it is not the days since they started.
 */
export function pasosDeLaLinea(
  e: Empleado,
  libros: PlanillaLinea,
  frecuencia: FrecuenciaPago,
  rango: Rango,
  rates: Pick<PayrollRates, 'inssLaboralBp'>,
  { escrito, pagada }: { escrito: boolean; pagada: boolean },
): PasoDeLaPlanilla[] {
  const pago = pagoDe(e);
  const dias = diasPagados(e, frecuencia, rango);
  const sueldo: PasoDeLaPlanilla =
    pago.porDia && pago.tarifa !== null && libros.dias !== undefined
      ? { clave: 'planilla.desglose.porDia', valores: { dias: formatQuantity(libros.dias as Milli), tarifa: formatMoney(pago.tarifa) }, monto: libros.bruto }
      : !pago.porDia && !escrito && dias !== null
        ? {
            clave: 'planilla.desglose.sueldoDias',
            valores: { dias: String(dias), de: String(diasDeLaCorrida(frecuencia, rango)), fecha: e.fechaIngreso },
            monto: libros.bruto,
          }
        : { clave: SALARIO_DE[frecuencia], monto: libros.bruto };
  const adelanto = libros.adelantoDescontado ?? 0;
  return [
    sueldo,
    ...pasoSi('planilla.extrasGravables', libros.otrosGravables, '+'),
    ...pasoSi('planilla.vacacionesPagadas', libros.vacacionesPagadas, '+'),
    ...pasoSi('planilla.aguinaldo', libros.aguinaldoPagado, '+'),
    ...pasoSi('planilla.desglose.inss', libros.inssLaboral, '−', { tasa: formatBp(rates.inssLaboralBp) }),
    ...pasoSi('planilla.desglose.ir', libros.irRetenido, '−'),
    // Their net, then the advances it takes off: what is handed over is less than it.
    ...(adelanto > 0 ? [{ signo: '=' as const, clave: 'planilla.desglose.neto' as const, monto: libros.neto }, ...pasoSi('planilla.desglose.adelantos', adelanto, '−')] : []),
    { signo: '=', clave: pagada ? 'planilla.desglose.entregado' : 'planilla.aEntregar', monto: subMoney(libros.neto, adelanto as Centavos) },
  ];
}

/** A run's lines added up, figure by figure (`pasosDelCosto`). */
export interface TotalesDeCorrida {
  /** Their pay: the salaries and whatever else the lines pay, before anything comes off. */
  brutoTotal: Centavos;
  inssLaboral: Centavos;
  irRetenido: Centavos;
  neto: Centavos;
  adelantos: Centavos;
  inssPatronal: Centavos;
  inatec: Centavos;
  provisionAguinaldo: Centavos;
  provisionVacaciones: Centavos;
  /** Of what the lines pay, what earlier runs set aside for it, and booked then. */
  deLoApartado: Centavos;
  /** What the run costs the business, as its entry books it (`costoDeLinea`). */
  costo: Centavos;
}

export function totalesDeCorrida(lineas: readonly PlanillaLinea[]): TotalesDeCorrida {
  const suma = (f: (l: PlanillaLinea) => number) => lineas.reduce((a, l) => a + f(l), 0) as Centavos;
  return {
    brutoTotal: suma((l) => l.brutoTotal),
    inssLaboral: suma((l) => l.inssLaboral),
    irRetenido: suma((l) => l.irRetenido),
    neto: suma((l) => l.neto),
    adelantos: suma((l) => l.adelantoDescontado ?? 0),
    inssPatronal: suma((l) => l.inssPatronal),
    inatec: suma((l) => l.inatec),
    provisionAguinaldo: suma((l) => l.provisionAguinaldo),
    provisionVacaciones: suma((l) => l.provisionVacaciones),
    deLoApartado: suma((l) => l.deLoApartado ?? 0),
    costo: suma(costoDeLinea),
  };
}

/**
 * What a run costs the business, a line each, as «Costo mensual» says a month: their pay; the
 * employer's INSS and INATEC; the twelfths set aside for the aguinaldo and the holidays; less what of
 * the pay earlier runs had set aside, already counted then (a rested holiday, the aguinaldo paid out).
 */
export function pasosDelCosto(tot: TotalesDeCorrida, rates: Pick<PayrollRates, 'inssPatronalBp' | 'inatecBp'>): PasoDeLaPlanilla[] {
  return [
    { clave: 'planilla.desglose.suPago', monto: tot.brutoTotal },
    ...pasoSi('planilla.calc.inssPatronal', tot.inssPatronal, '+', { tasa: formatBp(rates.inssPatronalBp) }),
    ...pasoSi('planilla.calc.inatec', tot.inatec, '+', { tasa: formatBp(rates.inatecBp) }),
    ...pasoSi('planilla.calc.aguinaldo', tot.provisionAguinaldo, '+'),
    ...pasoSi('planilla.calc.vacaciones', tot.provisionVacaciones, '+'),
    ...pasoSi('planilla.desglose.yaApartado', tot.deLoApartado, '−'),
    { signo: '=', clave: 'planilla.desglose.costoTotal', monto: tot.costo },
  ];
}
